// Program Documents (/program-documents): the Comms Briefs and Work Orders for every program the
// user can see, plus the "new document" form that composes a draft from the program's IO and QA
// rules. Admins also maintain the DemandArc leadership roster that every brief's Teams section
// starts with. Opened from the Programs page's "Docs" button as ?program=<id>.
// Backend: /program-documents. The editor is pages/ProgramDocumentEditor.js.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SearchableSelect from '../components/SearchableSelect';
import Section from '../components/Section';
import { useCurrentUser } from '../context/UserContext';
import { buildProgramOptionGroups, buildStatusGroupMap } from '../utils/statusGroups';
import { DOC_TYPE_LABELS, MAX_EXTRA_PROGRAMS, addExtraProgram, exportFileName } from '../utils/programDocuments';
import { downloadFileBlob } from '../utils/ciqAssets';

const API = process.env.REACT_APP_API_URL;

const PRESET_OPTIONS = [
  ['deal_accelerator', 'Deal Accelerator'],
  ['decision_builder', 'Decision Builder'],
  ['content_syndication', 'Content Syndication'],
  ['custom', 'Custom'],
];
const INPUT = {
  padding: '6px 8px', border: '1px solid #ccd4dd', borderRadius: '4px', fontSize: '13px',
  fontFamily: 'inherit', boxSizing: 'border-box',
};
const btn = (bg, disabled) => ({
  padding: '7px 14px', background: disabled ? '#bdc3c7' : bg, color: '#fff', border: 'none',
  borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600,
});
const th = { textAlign: 'left', fontSize: '12px', color: '#757575', padding: '6px 8px', borderBottom: '1px solid #e0e4e8' };
const td = { padding: '6px 8px', fontSize: '13px', borderBottom: '1px solid #f0f2f4', verticalAlign: 'middle' };

const errorText = (e) => (e?.response?.data?.detail && typeof e.response.data.detail === 'string')
  ? e.response.data.detail : (e?.message || 'Something went wrong');
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '');

function RosterPanel() {
  const [members, setMembers] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/program-documents/team-roster`).then(r => setMembers(r.data)).catch(e => setMsg(errorText(e)));
  }, []);

  const update = (i, patch) => setMembers(ms => ms.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await axios.put(`${API}/program-documents/team-roster`, { members });
      setMembers(r.data); setMsg('Roster saved. New comms briefs will use it.');
    } catch (e) { setMsg(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <Section title="DemandArc team roster" count={members ? members.filter(m => m.active).length : null} countColor="#95a5a6">
      <div style={{ fontSize: '12px', color: '#5d6d7e', marginBottom: '8px', lineHeight: 1.5 }}>
        These people appear first on the Teams section of every new Comms Brief, followed by the program's own
        Program Manager and supporting PMs. Changing the roster doesn't touch briefs that already exist; edit those in the brief.
      </div>
      {members === null ? <div style={{ color: '#757575' }}>Loading…</div> : (
        <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '8px' }}>
          <thead><tr><th style={th}>Name</th><th style={th}>Title</th><th style={th}>Email</th><th style={th}>Show</th><th style={th} /></tr></thead>
          <tbody>
            {members.map((m, i) => (
              <tr key={m.id || `new-${i}`}>
                <td style={td}><input value={m.name} style={{ ...INPUT, width: '100%' }} onChange={e => update(i, { name: e.target.value })} /></td>
                <td style={td}><input value={m.title} style={{ ...INPUT, width: '100%' }} onChange={e => update(i, { title: e.target.value })} /></td>
                <td style={td}><input value={m.email} style={{ ...INPUT, width: '100%' }} onChange={e => update(i, { email: e.target.value })} /></td>
                <td style={td}><input type="checkbox" checked={m.active} onChange={e => update(i, { active: e.target.checked })} /></td>
                <td style={td}>
                  <button type="button" style={{ ...btn('#c0392b'), padding: '3px 10px', fontSize: '12px' }}
                    onClick={() => setMembers(ms => ms.filter((_, j) => j !== i))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button type="button" style={btn('#7f8c8d')} onClick={() => setMembers(ms => [...(ms || []), { name: '', title: '', email: '', active: true }])}>+ Add person</button>
        <button type="button" style={btn('#27ae60', busy || members === null)} disabled={busy || members === null} onClick={save}>{busy ? 'Saving…' : 'Save roster'}</button>
        {msg && <span style={{ fontSize: '12px', color: '#5d6d7e' }}>{msg}</span>}
      </div>
    </Section>
  );
}

export default function ProgramDocuments() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [docs, setDocs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [programId, setProgramId] = useState(params.get('program') || '');
  const [docType, setDocType] = useState('comms_brief');
  const [preset, setPreset] = useState('decision_builder');
  const [briefs, setBriefs] = useState([]);
  const [parentId, setParentId] = useState('');
  const [extraIds, setExtraIds] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const loadDocs = useCallback(() => {
    axios.get(`${API}/program-documents/`).then(r => setDocs(r.data)).catch(e => setError(errorText(e)));
  }, []);

  useEffect(() => {
    loadDocs();
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data)).catch(() => {});
  }, [loadDocs]);

  // A work order is built from a comms brief: list the program's briefs so the PM can pick one.
  useEffect(() => {
    setExtraIds(ids => ids.filter(x => x !== String(programId)));
  }, [programId]);

  useEffect(() => {
    setParentId(''); setBriefs([]);
    if (docType !== 'work_order' || !programId) return;
    axios.get(`${API}/program-documents/?program_id=${programId}&doc_type=comms_brief`)
      .then(r => setBriefs(r.data)).catch(() => {});
  }, [docType, programId]);

  const groups = buildProgramOptionGroups(programs, buildStatusGroupMap(programStatuses));
  const programName = id => (programs.find(p => String(p.id) === String(id)) || {}).program_name || `Program ${id}`;
  const without = opts => opts.filter(o => o.value !== String(programId) && !extraIds.includes(o.value));

  const create = async () => {
    setBusy('create'); setError('');
    try {
      const body = { program_id: Number(programId), doc_type: docType, preset };
      if (docType === 'work_order' && parentId) body.parent_id = Number(parentId);
      if (extraIds.length) body.extra_program_ids = extraIds.map(Number);
      const r = await axios.post(`${API}/program-documents/draft`, body);
      navigate(`/program-documents/${r.data.id}`);
    } catch (e) { setError(errorText(e)); setBusy(''); }
  };

  const download = async (d, format) => {
    setError('');
    try {
      await downloadFileBlob(`${API}/program-documents/${d.id}/export?format=${format}`,
        exportFileName(d.doc_type, d.title || d.program_name, d.version, format));
    } catch (e) { setError(errorText(e)); }
  };

  const shown = docs.filter(d => (!filterType || d.doc_type === filterType)
    && (!params.get('program') || String(d.program_id) === params.get('program')));
  const newestBrief = briefs.length ? briefs[0] : null;

  return (
    <div style={{ maxWidth: '1100px' }}>
      <h2 style={{ marginTop: 0, color: '#2c3e50' }}>Program Documents</h2>
      <div style={{ fontSize: '13px', color: '#5d6d7e', marginBottom: '14px', lineHeight: 1.5 }}>
        Create a Comms Brief or Work Order from a program's IO and QA rules, edit it, then download it as Word or PDF.
        A Work Order is also built from the program's Comms Brief, so make the brief first.
      </div>

      {error && <div style={{ background: '#fdecea', border: '1px solid #e6b0aa', color: '#922b21', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}

      <div style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c3e50', marginBottom: '10px' }}>New document</div>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: '280px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>Program</div>
            <SearchableSelect options={groups.options} closedOptions={groups.closedOptions}
              value={programId} onChange={setProgramId} placeholder="— search or select a program —" />
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>Document</div>
            <select value={docType} style={INPUT} onChange={e => setDocType(e.target.value)}>
              {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>Program type</div>
            <select value={preset} style={INPUT} onChange={e => setPreset(e.target.value)}>
              {PRESET_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {docType === 'work_order' && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>Built from brief</div>
              <select value={parentId} style={INPUT} onChange={e => setParentId(e.target.value)} disabled={!programId}>
                <option value="">{newestBrief ? 'Latest Final (or newest) brief' : 'No brief yet, use IO and QA rules only'}</option>
                {briefs.map(b => <option key={b.id} value={b.id}>v{b.version} ({b.status})</option>)}
              </select>
            </div>
          )}
          <button type="button" style={btn('#27ae60', !programId || !!busy)} disabled={!programId || !!busy} onClick={create}>
            {busy === 'create' ? 'Building…' : `Create ${DOC_TYPE_LABELS[docType]}`}
          </button>
        </div>
        {!!programId && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '3px' }}>Also cover these programs (optional)</div>
            <div style={{ fontSize: '12px', color: '#5d6d7e', marginBottom: '6px', lineHeight: 1.5 }}>
              For one document that spans several programs. The document belongs to the program above; the others add
              their components and goals, so a Work Order still lists each component and goal.
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {extraIds.map(id => (
                <span key={id} style={{ background: '#eaf1f8', border: '1px solid #c5d6e8', borderRadius: '12px', padding: '3px 10px', fontSize: '12px' }}>
                  {programName(id)}
                  <span role="button" aria-label={`Remove ${programName(id)}`} style={{ marginLeft: '6px', cursor: 'pointer', color: '#922b21' }}
                    onClick={() => setExtraIds(extraIds.filter(x => x !== id))}>×</span>
                </span>
              ))}
              {extraIds.length < MAX_EXTRA_PROGRAMS && (
                <div style={{ minWidth: '280px' }}>
                  <SearchableSelect options={without(groups.options)} closedOptions={without(groups.closedOptions)}
                    value="" onChange={v => v && setExtraIds(addExtraProgram(extraIds, programId, v))}
                    placeholder="— add another program —" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#2c3e50' }}>
          {params.get('program') ? 'Documents for this program' : 'All documents'}
        </div>
        <select value={filterType} style={INPUT} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {params.get('program') && <span onClick={() => navigate('/program-documents')}
          style={{ fontSize: '12px', color: '#2e6bb5', cursor: 'pointer' }}>Show all programs</span>}
      </div>

      <div style={{ background: 'white', border: '1px solid #e0e4e8', borderRadius: '8px', overflowX: 'auto', marginBottom: '16px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr><th style={th}>Program</th><th style={th}>Document</th><th style={th}>Version</th><th style={th}>Type</th>
              <th style={th}>Status</th><th style={th}>Updated</th><th style={th} /></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td style={{ ...td, color: '#757575' }} colSpan={7}>No documents yet.</td></tr>}
            {shown.map(d => (
              <tr key={d.id}>
                <td style={td}>{d.program_name}</td>
                <td style={td}>{DOC_TYPE_LABELS[d.doc_type]}</td>
                <td style={td}>v{d.version}</td>
                <td style={td}>{(PRESET_OPTIONS.find(p => p[0] === d.preset) || [])[1] || d.preset}</td>
                <td style={td}>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '10px',
                    background: d.status === 'Final' ? '#e6f4ea' : '#fdf2e0', color: d.status === 'Final' ? '#1e6b43' : '#b9770e' }}>{d.status}</span>
                </td>
                <td style={td}>{fmtDate(d.updated_at)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button type="button" style={{ ...btn('#3498db'), padding: '3px 10px', fontSize: '12px', marginRight: '6px' }}
                    onClick={() => navigate(`/program-documents/${d.id}`)}>Open</button>
                  <button type="button" style={{ ...btn('#2e6bb5'), padding: '3px 10px', fontSize: '12px', marginRight: '6px' }}
                    onClick={() => download(d, 'docx')}>Word</button>
                  <button type="button" style={{ ...btn('#2e6bb5'), padding: '3px 10px', fontSize: '12px' }}
                    onClick={() => download(d, 'pdf')}>PDF</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isAdmin && <RosterPanel />}
    </div>
  );
}
